import * as Sentry from '@sentry/react';
import React from 'react';
import { createRoot } from 'react-dom/client';
import { initializeDevBridgeOverrides } from '@/services/devBridge';
import { getChunkRecoveryUrl } from '@/services/chunkRecovery';
import { App } from './app';
import './styles.css';
import './i18n';

declare const __BUILD_VERSION__: string | undefined;

Sentry.init({
  dsn:
    import.meta.env.VITE_SENTRY_DSN ??
    'https://6fe7cef04208871d6f4fce0ba44c15db@o588365.ingest.us.sentry.io/4511116189106176',
  sendDefaultPii: true,
  environment: import.meta.env.MODE,
  release: typeof __BUILD_VERSION__ === 'string' ? __BUILD_VERSION__ : undefined,
  debug: import.meta.env.DEV
});

let chunkRecoveryScheduled = false;
window.addEventListener('vite:preloadError', (event) => {
  const error = (event as Event & { payload: unknown }).payload;
  const recoveryUrl = getChunkRecoveryUrl(
    error,
    window.location.href,
    typeof __BUILD_VERSION__ === 'string' ? __BUILD_VERSION__ : 'unknown',
  );
  Sentry.setContext('asset_loading', {
    route: window.location.pathname,
    recovery: recoveryUrl ? 'reload' : 'unavailable',
    online: navigator.onLine,
  });
  Sentry.captureException(error, { tags: { phase: 'dynamic-import' } });
  if (!recoveryUrl || chunkRecoveryScheduled) return;
  chunkRecoveryScheduled = true;
  // Preserve the original exception for the ErrorBoundary and flush before navigation.
  const reload = () => window.location.replace(recoveryUrl);
  void Sentry.flush(1000).then(reload, reload);
});

initializeDevBridgeOverrides();

const container = document.getElementById('root');
if (!container) {
  throw new Error('Root container not found');
}

const root = createRoot(container);
root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>
);
